import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { examAPI, attemptAPI, monitoringAPI } from "../../services/api";
import toast from "react-hot-toast";
import ACCAInterface from "./ACCAInterface";
import InternshipInterface from "./InternshipInterface";
import { useAuth } from "../../contexts/AuthContext";
import socketService from "../../services/socketService";
import {
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiSend,
  FiFlag,
  FiBook,
  FiEdit2,
  FiAlignLeft,
  FiType,
  FiImage,
  FiInfo,
  FiGrid,
  FiX,
  FiMenu,
  FiSettings,
  FiHelpCircle,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import {
  FaRegCircle,
  FaCircle,
  FaRegFlag,
  FaFlag,
  FaCalculator,
  FaTrophy,
  FaTimes,
  FaBars,
} from "react-icons/fa";

// Helper function to detect ACCA exams
const isACCAExam = (examTitle) => {
  if (!examTitle) return false;
  const accaPatterns = [
    "FA1",
    "MA1",
    "FA2",
    "MA2",
    "F1",
    "F2",
    "F3",
    "F4",
    "F5",
    "F6",
    "F7",
    "F8",
    "F9",
  ];
  return accaPatterns.some((pattern) =>
    examTitle.toUpperCase().includes(pattern.toUpperCase())
  );
};

// Helper function to detect IELTS exams
const isIELTSExam = (examTitle) => {
  if (!examTitle) return false;
  return examTitle.toUpperCase().includes("IELTS");
};

// Helper function to detect TOEFL exams
const isTOEFLExam = (examTitle) => {
  if (!examTitle) return false;
  return examTitle.toUpperCase().includes("TOEFL");
};

// Helper function to detect Internship exams
const checkIsInternshipExam = (examTitle) => {
  if (!examTitle) return false;
  return examTitle.toLowerCase().includes("internship");
};

const ExamInterface = () => {
  // console.log('🎯 ExamInterface Component - MOUNTING');
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // console.log('🎯 ExamInterface - testId:', testId);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(2160); // 36 minutes in seconds
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [attemptId, setAttemptId] = useState(null);

  // Exam Interface States
  const [isAccaExam, setIsAccaExam] = useState(false);
  const [isIeltsExam, setIsIeltsExam] = useState(false);
  const [isToeflExam, setIsToeflExam] = useState(false);
  const [isInternshipExam, setIsInternshipExam] = useState(false);
  const [accaInterfaceMode, setAccaInterfaceMode] = useState("question"); // question, review, calculator
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showQuestionNavigator, setShowQuestionNavigator] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [examProgress, setExamProgress] = useState(0);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [blurCount, setBlurCount] = useState(0);
  const [showWebcamConsent, setShowWebcamConsent] = useState(false);

  // Add effect to log when showWebcamConsent changes
  useEffect(() => {
    console.log("showWebcamConsent state changed:", showWebcamConsent);
  }, [showWebcamConsent]);

  // Submit exam attempt mutation
  const submitAttemptMutation = useMutation({
    mutationFn: (data) =>
      attemptAPI.submitAttempt(data.attemptId, data.responses, data.name),
    onSuccess: (data) => {
      console.log("🔍 Submit attempt success:", data);
      setIsSubmitted(true);
      console.log("Exam submitted successfully!");

      const results = data.data?.data?.results;
      if (results) {
        const score = results.percentage || 0;
        const grade = results.grade || "N/A";
        const correctAnswers = results.correctAnswers || 0;
        const totalQuestions = results.totalQuestions || 0;

        console.log(
          `Exam completed! Score: ${score.toFixed(
            1
          )}% (${grade}) - ${correctAnswers}/${totalQuestions} correct`
        );
      }

      const navAttemptId = data?.data?.data?.attempt?.id || attemptId;
      const examTitleLower = (exam?.title || "").toLowerCase();
      const isInternshipTitle =
        examTitleLower.includes("internship") ||
        examTitleLower.includes("intership");
      if (isInternshipExam || isInternshipTitle) {
        navigate(`/exam/thanks/${navAttemptId}`);
      } else {
        navigate(`/exam/results/${navAttemptId}`);
      }
    },
    onError: (error) => {
      console.error("Failed to submit exam");
      console.error("Submit exam error:", error);

      const errorMessage =
        error.response?.data?.message || "Failed to submit exam";
      toast.error(errorMessage);
    },
  });
  const [webcamStream, setWebcamStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const webcamVideoRef = useRef(null);
  const screenVideoRef = useRef(null);
  const webcamIntervalRef = useRef(null);
  const screenIntervalRef = useRef(null);
  const [showFullScreenQuestion, setShowFullScreenQuestion] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // Enter fullscreen automatically for all exams
  useEffect(() => {
    setShowFullScreenQuestion(true);
  }, []);

  // Do not show instructions automatically
  useEffect(() => {
    setShowInstructions(false);
  }, []);

  // Additional safeguard to ensure instructions are never shown
  useEffect(() => {
    if (showInstructions) {
      console.log("🎯 Forcing showInstructions to false");
      setShowInstructions(false);
    }
  }, [showInstructions]);
  // Fetch real exam data
  const [examData, setExamData] = useState(null);
  const [examLoading, setExamLoading] = useState(true);
  const [examError, setExamError] = useState(null);

  console.log("ExamInterface component rendered with examData:", examData);

  // Dummy data for testing
  // const dummyExamData = {
  //   data: {
  //     data: {
  //       exam: {
  //         id: testId || "dummy-exam-1",
  //         title: "HUIGLHJL;",
  //         description: "Sample Mathematics Test",
  //         duration: 60,
  //         totalQuestions: 5,
  //         totalMarks: 100,
  //       },
  //       questions: [
  //         {
  //           id: 1,
  //           question: "Which of the following is a trigonometric identity?",
  //           options: [
  //             "sin²x + cos²x = 1",
  //             "sin²x - cos²x = 1",
  //             "tan²x + sec²x = 1",
  //             "cos²x + sin²x = 0",
  //           ],
  //           correctAnswer: 0,
  //           marks: 20,
  //           type: "multiple_choice",
  //         },
  //         {
  //           id: 2,
  //           question: "What is the value of sin(30°)?",
  //           options: ["1/2", "√3/2", "1/√2", "1"],
  //           correctAnswer: 0,
  //           marks: 20,
  //           type: "multiple_choice",
  //         },
  //         {
  //           id: 3,
  //           question:
  //             "Which trigonometric function is the reciprocal of cosine?",
  //           options: ["sine", "tangent", "secant", "cosecant"],
  //           correctAnswer: 2,
  //           marks: 20,
  //           type: "multiple_choice",
  //         },
  //         {
  //           id: 4,
  //           question: "What is the period of the function y = sin(x)?",
  //           options: ["π", "2π", "π/2", "4π"],
  //           correctAnswer: 1,
  //           marks: 20,
  //           type: "multiple_choice",
  //         },
  //         {
  //           id: 5,
  //           question: "Which of the following is the derivative of cos(x)?",
  //           options: ["sin(x)", "-sin(x)", "cos(x)", "-cos(x)"],
  //           correctAnswer: 1,
  //           marks: 20,
  //           type: "multiple_choice",
  //         },
  //       ],
  //       attempt: {
  //         id: "dummy-attempt-1",
  //         duration: 60,
  //       },
  //     },
  //   },
  // };

  // Use dummy data if there's an error or no data
  const finalExamData = examData;
  const finalExam = finalExamData?.data?.data?.exam || {};
  const finalQuestions = finalExamData?.data?.data?.questions || [];

  // Extract exam data - moved to top to avoid lexical declaration errors
  const exam = finalExam;
  const questions = finalQuestions;

  // Questions come directly from the start attempt response
  const effectiveQuestions = questions || [];
  const currentQuestionData =
    effectiveQuestions &&
    effectiveQuestions.length > 0 &&
    currentQuestion < effectiveQuestions.length
      ? effectiveQuestions[currentQuestion]
      : null;

  // Compute exam title once for type detection to avoid infinite loops
  const examTitle = exam?.title || "";

  // Infer exam type flags from exam title (real or dummy), run only when title changes
  // Treat all exams as internship exams by default
  useEffect(() => {
    if (!examTitle) return;
    const acca = isACCAExam(examTitle);
    const ielts = isIELTSExam(examTitle);
    const toefl = isTOEFLExam(examTitle);
    // Treat all exams as internship exams
    const internship = true; // checkIsInternshipExam(examTitle);
    setIsAccaExam(acca);
    setIsIeltsExam(ielts);
    setIsToeflExam(toefl);
    setIsInternshipExam(internship);
    // Do not automatically show fullscreen for any exam type
    // setShowFullScreenQuestion logic is handled elsewhere
  }, [examTitle]);
  useEffect(() => {
    if (testId && !examData) {
      // Only fetch if we don't have exam data yet
      console.log("🔍 useEffect - Fetching exam data for testId:", testId);
      console.log("🔍 examAPI object:", examAPI);
      console.log("🔍 examAPI.getExamDetails:", examAPI?.getExamDetails);
      console.log("🔍 Current examData:", examData);
      setExamLoading(true);
      setExamError(null);

      console.log(
        "🔍 About to call attemptAPI.startAttempt with testId:",
        testId
      );
      console.log("🔍 attemptAPI object:", attemptAPI);
      console.log(
        "🔍 attemptAPI.startAttempt function:",
        attemptAPI?.startAttempt
      );
      console.log(
        "🔍 Full URL will be:",
        `https://31.97.70.79:5050/api/v1/exams/${testId}/start`
      );
      console.log(
        "🔍 Auth token:",
        localStorage.getItem("token") ? "exists" : "missing"
      );
      console.log(
        "🔍 User data:",
        localStorage.getItem("user") ? "exists" : "missing"
      );

      if (!attemptAPI || !attemptAPI.startAttempt) {
        console.error(
          "🔍 attemptAPI or startAttempt function is not available!"
        );
        setExamError(new Error("attemptAPI not available"));
        setExamLoading(false);
        return;
      }

      // Add timeout to the API call
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error("API call timeout after 10 seconds")),
          10000
        );
      });

      Promise.race([attemptAPI.startAttempt(testId), timeoutPromise])
        .then((response) => {
          console.log("🔍 API Response:", response);
          console.log("🔍 Response data structure:", {
            data: response.data,
            "data.data": response.data?.data,
            "data.data.exam": response.data?.data?.exam,
            "data.exam": response.data?.exam,
          });
          setExamData(response);

          // Check exam type
          const examTitle = response.data?.data?.exam?.title;
          const isAcca = examTitle ? isACCAExam(examTitle) : false;
          const isIelts = examTitle ? isIELTSExam(examTitle) : false;
          const isToefl = examTitle ? isTOEFLExam(examTitle) : false;
          // Treat all exams as internship exams
          const isInternship = true; // examTitle ? checkIsInternshipExam(examTitle) : false;

          console.log("🎯 Exam data structure:", {
            examTitle,
            examId: response.data?.data?.exam?.id,
            examData: response.data?.data?.exam,
            questionsCount: response.data?.data?.questions?.length,
            isAcca,
            isIelts,
            isToefl,
            isInternship,
          });

          setIsAccaExam(isAcca);
          setIsIeltsExam(isIelts);
          setIsToeflExam(isToefl);
          setIsInternshipExam(isInternship);

          // Set attempt ID and time from the start attempt response
          const newAttemptId = response.data?.data?.attempt?.id;
          setAttemptId(newAttemptId);
          setTimeLeft(2160); // Set to 36 minutes (2160 seconds)

          // Start monitoring via WebSocket and REST (best-effort)
          try {
            const userId = user?.id;
            const userName =
              [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
              user?.email ||
              "Unknown User";

            console.log("📡 Emitting start-exam-monitoring via WebSocket:", {
              attemptId: newAttemptId,
              userId,
              userName,
            });

            socketService.emitStartExamMonitoring({
              attemptId: newAttemptId,
              userId,
              userName,
            });

            // Fire-and-forget call to mark monitoring started on the backend
            if (newAttemptId) {
              monitoringAPI.startMonitoring(newAttemptId).catch((err) => {
                console.error("Error starting monitoring via API:", err);
                // Silently handle monitoring start errors to prevent toast notifications
              });
            }          } catch (monitorErr) {
            console.error("Error starting monitoring (frontend):", monitorErr);
          }

          // Skip webcam consent modal - start exam directly
          console.log("Skipping webcam consent and starting exam directly");
          setShowWebcamConsent(false);

          // Do not show instructions for any exam type
          setShowInstructions(false);

          if (isAcca) {
            console.log("🎯 ACCA Exam Detected:", examTitle);
            console.log("🎯 ACCA Exam Data:", {
              examTitle,
              questionsCount:
                response.data?.data?.questions?.length ||
                response.data?.questions?.length,
              examDuration:
                response.data?.data?.exam?.duration ||
                response.data?.exam?.duration,
              examTotalQuestions:
                response.data?.data?.exam?.totalQuestions ||
                response.data?.exam?.totalQuestions,
            });
            // Set ACCA-specific defaults
            setAccaInterfaceMode("question");
            setShowFullScreenQuestion(false); // Do not show fullscreen interface immediately
          } else if (isIelts) {
            console.log("🎯 IELTS Exam Detected:", examTitle);
            // Set IELTS-specific defaults
          } else if (isToefl) {
            console.log("🎯 TOEFL Exam Detected:", examTitle);
            // Set TOEFL-specific defaults
          } else if (isInternship) {
            console.log("🎯 Internship Exam Detected:", examTitle);
            // Set Internship-specific defaults
            setShowFullScreenQuestion(true); // Show fullscreen interface immediately for internship exams
          } else {
            console.log("🎯 Regular Exam Detected:", examTitle);
            // Set regular exam defaults - show fullscreen for all exams now
            setShowFullScreenQuestion(true);
          }
          setExamLoading(false);
        })
        .catch((error) => {
          console.error("🔍 API Error:", error);
          console.error("🔍 API Error details:", {
            message: error.message,
            response: error.response,
            status: error.response?.status,
            data: error.response?.data,
            config: error.config,
            url: error.config?.url,
            method: error.config?.method,
          });
          setExamError(error);
          setExamLoading(false);
        });
    }
  }, [testId]);

  // console.log('🔍 React Query Debug:', {
  //   testId,
  //   enabled: !!testId,
  //   examLoading,
  //   examError
  // });

  // Allow F11 key functionality
  useEffect(() => {
    // F11 key is now allowed for fullscreen functionality
    return () => {};
  }, []);

  // Do not monitor fullscreen changes
  useEffect(() => {
    // Fullscreen monitoring has been disabled
    return () => {};
  }, []);

  // Monitor tab/window blur and visibility changes via monitoring API
  useEffect(() => {
    const handleBlur = () => {
      if (!attemptId || isSubmitted) return;
      setBlurCount((c) => c + 1);

      try {
        monitoringAPI
          .logEvent(attemptId, "WINDOW_BLUR", {
            timestamp: new Date().toISOString(),
          })
          .catch((err) => {
            console.error("Error logging WINDOW_BLUR via monitoring API:", err);
          });
      } catch (err) {
        console.error("Error logging blur event:", err);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && attemptId && !isSubmitted) {
        setBlurCount((c) => c + 1);
        try {
          monitoringAPI
            .logEvent(attemptId, "VISIBILITY_HIDDEN", {
              timestamp: new Date().toISOString(),
            })
            .catch((err) => {
              console.error(
                "Error logging VISIBILITY_HIDDEN via monitoring API:",
                err
              );
            });
        } catch (err) {
          console.error("Error logging visibility event:", err);
        }
      }
    };

    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [attemptId, isSubmitted]);

  // Start webcam and screen capture after user consents
  const handleEnableWebcam = async () => {
    try {
      console.log("Attempting to access webcam and screen...");

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error("Webcam not supported in this browser");
        setShowWebcamConsent(false);
        return;
      }

      // Check if we're in a secure context
      if (!window.isSecureContext) {
        console.warn(
          "Not in a secure context. Media access may be restricted."
        );
      }

      // Request webcam access
      console.log("Requesting webcam access with constraints:", {
        video: true,
        audio: false,
      });
      const webcamStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });

      console.log("Webcam access granted. Stream:", webcamStream);
      setWebcamStream(webcamStream);

      // Request screen sharing access
      console.log("Requesting screen sharing access");
      try {
        // @ts-ignore - getDisplayMedia is not in the TypeScript definitions yet
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false,
        });

        console.log("Screen sharing access granted. Stream:", screenStream);
        setScreenStream(screenStream);

        // Handle screen sharing ended
        screenStream.getVideoTracks()[0].addEventListener("ended", () => {
          console.log("Screen sharing ended by user");
          setScreenStream(null);

          // Log this event
          if (attemptId) {
            monitoringAPI
              .logEvent(attemptId, "SCREEN_SHARING_ENDED", {
                timestamp: new Date().toISOString(),
              })
              .catch((err) => {
                console.error(
                  "Error logging SCREEN_SHARING_ENDED via monitoring API:",
                  err
                );
              });
          }
        });
      } catch (screenError) {
        console.warn("Screen sharing not available or denied:", screenError);

        // Log this event
        if (attemptId) {
          monitoringAPI
            .logEvent(attemptId, "SCREEN_SHARING_DENIED", {
              message: screenError.message || String(screenError),
              timestamp: new Date().toISOString(),
            })
            .catch((err) => {
              console.error(
                "Error logging SCREEN_SHARING_DENIED via monitoring API:",
                err
              );
            });
        }
      }

      setShowWebcamConsent(false);
      console.log("Webcam and screen streams set and consent modal hidden");
    } catch (error) {
      console.error("Error accessing webcam:", error);
      console.error("Error name:", error.name);
      console.error("Error message:", error.message);
      setShowWebcamConsent(false);

      const userId = user?.id;
      const attempt = attemptId;

      try {
        socketService.emitMonitoringError({
          attemptId: attempt,
          userId,
          error: error.message || String(error),
        });
      } catch (err) {
        console.error("Error emitting monitoring error:", err);
      }

      try {
        if (attempt) {
          monitoringAPI
            .logEvent(attempt, "WEBCAM_ERROR", {
              message: error.message || String(error),
              timestamp: new Date().toISOString(),
            })
            .catch((err) => {
              console.error(
                "Error logging WEBCAM_ERROR via monitoring API:",
                err
              );
            });
        }
      } catch (err) {
        console.error("Error logging webcam error:", err);
      }
    }
  };

  // Stop webcam stream and interval
  const stopWebcam = () => {
    try {
      if (webcamIntervalRef.current) {
        clearInterval(webcamIntervalRef.current);
        webcamIntervalRef.current = null;
      }
      if (webcamStream) {
        webcamStream.getTracks().forEach((track) => track.stop());
      }
      setWebcamStream(null);
    } catch (err) {
      console.error("Error stopping webcam:", err);
    }
  };

  // Start sending webcam frames when stream is active
  useEffect(() => {
    console.log("Webcam stream effect triggered with:", {
      webcamStream: !!webcamStream,
      attemptId,
      isSubmitted,
    });

    if (!webcamStream || !attemptId || isSubmitted) {
      console.log("Webcam stream effect early exit:", {
        hasStream: !!webcamStream,
        hasAttemptId: !!attemptId,
        isSubmitted,
      });
      return;
    }

    console.log("Setting up webcam stream processing");
    const videoEl = webcamVideoRef.current;
    if (videoEl) {
      videoEl.srcObject = webcamStream;
      videoEl.play().catch((err) => {
        console.error("Error playing webcam video element:", err);
      });
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    webcamIntervalRef.current = setInterval(() => {
      if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) {
        return;
      }

      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

      try {
        const frameData = canvas.toDataURL("image/jpeg", 0.5);
        const payload = {
          attemptId,
          userId: user?.id,
          stream: frameData, // Changed from 'frame' to 'stream' to match backend expectations
          timestamp: new Date().toISOString(),
        };
        console.log("📡 Emitting webcam-stream frame via WebSocket:", {
          attemptId: payload.attemptId,
          userId: payload.userId,
          frameLength: payload.stream?.length,
          timestamp: payload.timestamp,
        });
        socketService.emitWebcamStream(payload);
      } catch (err) {
        console.error("Error capturing/sending webcam frame:", err);
      }
    }, 2000); // send a frame every 2 seconds

    return () => {
      console.log("Cleaning up webcam stream interval");
      if (webcamIntervalRef.current) {
        clearInterval(webcamIntervalRef.current);
        webcamIntervalRef.current = null;
      }
      // Do not stop the stream here; stopWebcam handles full cleanup.
    };
  }, [webcamStream, attemptId, isSubmitted, user?.id]);

  // Start sending screen frames when stream is active
  useEffect(() => {
    if (!screenStream || !attemptId || isSubmitted) {
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    screenIntervalRef.current = setInterval(() => {
      // Create a temporary video element to capture screen frames
      const videoEl = document.createElement("video");
      videoEl.srcObject = screenStream;
      videoEl
        .play()
        .then(() => {
          canvas.width = videoEl.videoWidth;
          canvas.height = videoEl.videoHeight;
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

          try {
            const frameData = canvas.toDataURL("image/jpeg", 0.5);
            const payload = {
              attemptId,
              userId: user?.id,
              stream: frameData,
              timestamp: new Date().toISOString(),
            };
            console.log("📡 Emitting screen-stream frame via WebSocket:", {
              attemptId: payload.attemptId,
              userId: payload.userId,
              frameLength: payload.stream?.length,
              timestamp: payload.timestamp,
            });
            socketService.emitScreenStream(payload);
          } catch (err) {
            console.error("Error capturing/sending screen frame:", err);
          }

          // Clean up temporary video element
          videoEl.remove();
        })
        .catch((err) => {
          console.error("Error playing screen video element:", err);
          videoEl.remove();
        });
    }, 2000); // send a frame every 2 seconds

    return () => {
      if (screenIntervalRef.current) {
        clearInterval(screenIntervalRef.current);
        screenIntervalRef.current = null;
      }
    };
  }, [screenStream, attemptId, isSubmitted, user?.id]);

  // Cleanup webcam when exam is submitted or component unmounts
  useEffect(() => {
    if (isSubmitted) {
      stopWebcam();
    }
    return () => {
      stopWebcam();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted]);

  // Debug logging for ACCA interface
  useEffect(() => {
    if (isAccaExam) {
      console.log("🎯 ACCA Interface Debug:", {
        isAccaExam,
        showFullScreenQuestion,
        questionsCount: questions?.length,
        effectiveQuestionsCount: effectiveQuestions?.length,
        currentQuestion,
        currentQuestionData,
        examTitle: exam?.title,
        timeLeft,
        examProgress,
        answersCount: Object.keys(answers).length,
      });
    }
  }, [
    isAccaExam,
    showFullScreenQuestion,
    questions,
    effectiveQuestions,
    currentQuestion,
    currentQuestionData,
    exam,
    timeLeft,
    examProgress,
    answers,
  ]);

  // Removed unnecessary getExamQuestions calls - questions come with start attempt

  // Removed manual fallback - questions come with start attempt

  // Removed immediate fallback - questions come with start attempt

  // Start exam attempt
  const startAttemptMutation = useMutation({
    mutationFn: (examId) => {
      console.log("🎯 Starting exam attempt mutation for:", examId);
      return attemptAPI.startAttempt(examId);
    },
    onSuccess: (data) => {
      console.log("🔍 Start attempt success:", data);
      console.log("🎯 Start attempt data structure:", {
        attemptId: data.data?.data?.attempt?.id,
        questions: data.data?.data?.questions,
        questionsCount: data.data?.data?.questions?.length,
        duration: data.data?.data?.attempt?.duration,
      });

      const newAttemptId = data.data?.data?.attempt?.id;
      console.log("New attempt ID:", newAttemptId);
      setAttemptId(newAttemptId);
      setTimeLeft(2160); // Set to 36 minutes (2160 seconds)

      // Start monitoring via WebSocket and REST (best-effort)
      try {
        const userId = user?.id;
        const userName =
          [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
          user?.email ||
          "Unknown User";

        console.log("📡 Emitting start-exam-monitoring via WebSocket:", {
          attemptId: newAttemptId,
          userId,
          userName,
        });

        socketService.emitStartExamMonitoring({
          attemptId: newAttemptId,
          userId,
          userName,
        });

        // Fire-and-forget call to mark monitoring started on the backend
        if (newAttemptId) {
          monitoringAPI.startMonitoring(newAttemptId).catch((err) => {
            console.error("Error starting monitoring via API:", err);
            // Silently handle monitoring start errors to prevent toast notifications
          });
        }      } catch (monitorErr) {
        console.error("Error starting monitoring (frontend):", monitorErr);
      }

      // Skip webcam consent and proceed directly to exam
      setShowWebcamConsent(false);

      // Get questions from the start attempt response
      const startQuestions = data.data?.data?.questions || [];
      if (startQuestions.length > 0) {
        console.log(
          "🎯 Questions loaded from start attempt:",
          startQuestions.length
        );
        setExamData((prev) => ({
          ...prev,
          data: {
            ...prev?.data,
            data: {
              ...prev?.data?.data,
              questions: startQuestions,
            },
          },
        }));
      } else {
        console.log("🎯 No questions in start attempt response");
      }

      // toast.success('Exam started successfully!'); // Commented out to prevent toast display in InternshipInterface
    },
    onError: (error) => {
      console.error("🎯 Start attempt error:", error);
      console.error("🎯 Start attempt error details:", {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
      });

      const errorMessage =
        error.response?.data?.message || "Failed to start exam";
      // toast.error(errorMessage); // Commented out to prevent toast display in InternshipInterface

      // If attempts exceeded, show detailed message
      if (
        error.response?.data?.attemptsUsed &&
        error.response?.data?.attemptsAllowed
      ) {
        // toast.error(
        //   `Attempts exceeded: ${error.response.data.attemptsUsed}/${error.response.data.attemptsAllowed} attempts used`,
        //   { duration: 5000 }
        // );
      }
    },
  });

  // Submit exam attempt
  // const submitAttemptMutation = useMutation({
  //   mutationFn: (data) => attemptAPI.submitAttempt(data.attemptId, data.responses),
  //   onSuccess: (data) => {
  //     console.log('🔍 Submit attempt success:', data);
  //     setIsSubmitted(true);
  //     toast.success('Exam submitted successfully!');
  //
  //     // Show detailed success message with analytics preview
  //     const results = data.data?.data?.results;
  //     if (results) {
  //       const score = results.percentage || 0;
  //       const grade = results.grade || 'N/A';
  //       const correctAnswers = results.correctAnswers || 0;
  //       const totalQuestions = results.totalQuestions || 0;
  //
  //       toast.success(
  //         `Exam completed! Score: ${score.toFixed(1)}% (${grade}) - ${correctAnswers}/${totalQuestions} correct`,
  //         { duration: 4000 }
  //       );
  //     }
  //
  //     // Navigate immediately after submission
  //     const navAttemptId = data?.data?.data?.attempt?.id || attemptId;
  //     const examTitle = (exam?.title || '').toLowerCase();
  //     const isInternshipTitle = examTitle.includes('internship') || examTitle.includes('intership');
  //     if (isInternshipExam || isInternshipTitle) {
  //       navigate(`/exam/thanks/${navAttemptId}`);
  //     } else {
  //       navigate(`/exam/results/${navAttemptId}`);
  //     }
  //   },
  //   onError: (error) => {
  //     toast.error('Failed to submit exam');
  //     console.error('Submit exam error:', error);
  //   }
  // });

  // ACCA-specific functions
  const toggleQuestionFlag = (questionIndex) => {
    const newFlaggedQuestions = new Set(flaggedQuestions);
    if (newFlaggedQuestions.has(questionIndex)) {
      newFlaggedQuestions.delete(questionIndex);
    } else {
      newFlaggedQuestions.add(questionIndex);
    }
    setFlaggedQuestions(newFlaggedQuestions);
  };

  // Handle answer selection for multiple choice questions
  const handleAnswerSelect = (questionId, answerIndex) => {
    console.log("🎯 handleAnswerSelect called:", { questionId, answerIndex });
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex,
    }));
  };

  const toggleQuestionNavigator = () => {
    console.log(
      "🔍 toggleQuestionNavigator called! Current state:",
      showQuestionNavigator
    );
    setShowQuestionNavigator(!showQuestionNavigator);
    setShowCalculator(false);
    setShowInstructions(false);
    console.log("🔍 Navigator state will be:", !showQuestionNavigator);
  };

  const toggleCalculator = () => {
    console.log("🔍 toggleCalculator called! Current state:", showCalculator);
    setShowCalculator(!showCalculator);
    setShowQuestionNavigator(false);
    setShowInstructions(false);
    console.log("🔍 Calculator state will be:", !showCalculator);
  };

  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
    setShowQuestionNavigator(false);
    setShowCalculator(false);
  };

  const toggleFullScreenQuestion = () => {
    setShowFullScreenQuestion(!showFullScreenQuestion);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const navigateToQuestion = (questionIndex) => {
    setCurrentQuestion(questionIndex);
    setShowQuestionNavigator(false);
    setShowFullScreenQuestion(true);
  };

  const calculateExamProgress = () => {
    if (!effectiveQuestions || effectiveQuestions.length === 0) return 0;
    return Math.round(
      (Object.keys(answers).length / effectiveQuestions.length) * 100
    );
  };

  // Calculator functionality
  const [calculatorDisplay, setCalculatorDisplay] = useState("0");
  const [calculatorMemory, setCalculatorMemory] = useState(null);
  const [calculatorOperation, setCalculatorOperation] = useState(null);
  const [calculatorWaitingForOperand, setCalculatorWaitingForOperand] =
    useState(false);

  const handleCalculatorInput = (value) => {
    if (value === "C") {
      setCalculatorDisplay("0");
      setCalculatorMemory(null);
      setCalculatorOperation(null);
      setCalculatorWaitingForOperand(false);
      return;
    }

    if (value === "±") {
      setCalculatorDisplay((prev) =>
        prev === "0" ? "0" : prev.startsWith("-") ? prev.slice(1) : `-${prev}`
      );
      return;
    }

    if (value === "%") {
      const currentValue = parseFloat(calculatorDisplay);
      setCalculatorDisplay(String(currentValue / 100));
      return;
    }

    if (["+", "-", "×", "÷"].includes(value)) {
      if (calculatorOperation && !calculatorWaitingForOperand) {
        const result = performCalculation();
        setCalculatorDisplay(String(result));
        setCalculatorMemory(result);
      } else {
        setCalculatorMemory(parseFloat(calculatorDisplay));
      }
      setCalculatorOperation(value);
      setCalculatorWaitingForOperand(true);
      return;
    }

    if (value === "=") {
      if (calculatorOperation && !calculatorWaitingForOperand) {
        const result = performCalculation();
        setCalculatorDisplay(String(result));
        setCalculatorMemory(null);
        setCalculatorOperation(null);
        setCalculatorWaitingForOperand(false);
      }
      return;
    }

    if (value === ".") {
      if (calculatorWaitingForOperand) {
        setCalculatorDisplay("0.");
        setCalculatorWaitingForOperand(false);
      } else if (calculatorDisplay.indexOf(".") === -1) {
        setCalculatorDisplay((prev) => prev + ".");
      }
      return;
    }

    // Number input
    if (calculatorWaitingForOperand) {
      setCalculatorDisplay(value);
      setCalculatorWaitingForOperand(false);
    } else {
      setCalculatorDisplay((prev) => (prev === "0" ? value : prev + value));
    }
  };

  const performCalculation = () => {
    const currentValue = parseFloat(calculatorDisplay);
    const memoryValue = calculatorMemory;

    switch (calculatorOperation) {
      case "+":
        return memoryValue + currentValue;
      case "-":
        return memoryValue - currentValue;
      case "×":
        return memoryValue * currentValue;
      case "÷":
        return memoryValue / currentValue;
      default:
        return currentValue;
    }
  };

  // Enhanced ACCA exam progress calculation
  useEffect(() => {
    setExamProgress(calculateExamProgress());
  }, [answers, questions]);

  // Removed duplicate start attempt logic - now handled in main useEffect

  useEffect(() => {
    if (!attemptId || isSubmitted) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [attemptId, isSubmitted]);

  // Remove duplicate - handleAnswerSelect is already defined above
  // const handleAnswerSelect = (questionId, answerIndex) => {
  //   setAnswers(prev => ({
  //     ...prev,
  //     [questionId]: answerIndex
  //   }));
  // };

  const handleFillInTheBlankAnswer = (
    questionId,
    answerDataOrBlankIndex,
    textAnswer
  ) => {
    setAnswers((prev) => {
      let newAnswers;

      // Legacy format: handleFillInTheBlankAnswer(questionId, blankIndex, textAnswer)
      // This is used by ACCA components that pass blankIndex + value.
      if (
        typeof answerDataOrBlankIndex === "number" &&
        textAnswer !== undefined
      ) {
        // Legacy format: handleFillInTheBlankAnswer(questionId, blankIndex, textAnswer)
        const blankIndex = answerDataOrBlankIndex;
        const currentAnswers = prev[questionId] || [];
        newAnswers = [...currentAnswers];
        newAnswers[blankIndex] = textAnswer;
      } else {
        // New format: handleFillInTheBlankAnswer(questionId, answerData)
        // answerData can be either an array or an object
        const answerData = answerDataOrBlankIndex;
        if (Array.isArray(answerData)) {
          // Already an array, use directly
          newAnswers = answerData;
        } else if (typeof answerData === "object" && answerData !== null) {
          // Convert object {0: "answer1", 1: "answer2"} to array ["answer1", "answer2"]
          newAnswers = Object.keys(answerData)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((key) => answerData[key] || "");
        } else if (answerData !== undefined) {
          // Single value, convert to array
          newAnswers = [answerData];
        } else {
          // Fallback: use existing answers or empty array
          newAnswers = prev[questionId] || [];
        }
      }

      return {
        ...prev,
        [questionId]: newAnswers,
      };
    });
  };

  const handleSubmit = (name) => {
    console.log(
      "🔍 handleSubmit - attemptId:",
      attemptId,
      "answers:",
      answers,
      "name:",
      name
    );
    console.log("🔍 handleSubmit - CALLED! This function is being executed");
    if (!attemptId) {
      // toast.error('No active exam attempt'); // Commented out to prevent toast display in InternshipInterface
      return;
    }

    // Show confirmation dialog
    const answeredCount = Object.keys(answers).length;
    const totalQuestions = effectiveQuestions.length;
    const unansweredCount = totalQuestions - answeredCount;

    let confirmMessage = `Are you sure you want to submit your assessment?\n\n📊 Progress: ${answeredCount}/${totalQuestions} questions answered`;

    if (unansweredCount > 0) {
      confirmMessage += `\n⚠️ ${unansweredCount} question(s) unanswered`;
    }

    confirmMessage += "\n\nThis action cannot be undone.";

    if (window.confirm(confirmMessage)) {
      // Convert answers to the format expected by the backend
      const responses = Object.entries(answers)
        .filter(([questionId]) => !questionId.endsWith("_other_text")) // Filter out "Other" text answers
        .map(([questionId, answer]) => {
          const currentQuestion = effectiveQuestions.find(
            (q) => q.id === questionId
          );

          console.log("🔍 Processing answer for question:", {
            questionId,
            questionType: currentQuestion?.type,
            answer,
            answerType: typeof answer,
            isArray: Array.isArray(answer),
          });

          if (
            currentQuestion?.type === "ESSAY" ||
            currentQuestion?.type === "SHORT_ANSWER"
          ) {
            // For essay/short answer questions, send as essayAnswer
            return {
              questionId,
              selectedOptions: [],
              essayAnswer: answer,
              timeSpent: 0,
            };
          } else if (currentQuestion?.type === "FILL_IN_THE_BLANK") {
            // For fill-in-the-blank questions, send as essayAnswer with proper format
            // The answer should be an array of text strings
            if (Array.isArray(answer)) {
              // Convert array of text answers to the format expected by backend
              const textAnswers = answer.filter(
                (text) => text && text.trim().length > 0
              );
              return {
                questionId,
                selectedOptions: [],
                essayAnswer: textAnswers.join(" | "), // Format: "answer1 | answer2 | answer3"
                timeSpent: 0,
              };
            } else {
              // Fallback if answer is not an array
              return {
                questionId,
                selectedOptions: [],
                essayAnswer: answer || "",
                timeSpent: 0,
              };
            }
          } else {
            // For multiple choice, single choice, true/false, etc.
            // Convert numeric indices to option IDs if needed
            let selectedOptions = [];
            if (Array.isArray(answer)) {
              selectedOptions = answer;
            } else if (typeof answer === "number" && currentQuestion?.options) {
              // Convert numeric index to option ID
              const selectedOption = currentQuestion.options[answer];
              if (selectedOption) {
                selectedOptions = [selectedOption.id];
              } else {
                selectedOptions = [answer]; // Fallback to original value
              }
            } else {
              selectedOptions = [answer];
            }

            // No additional processing needed for regular multiple choice questions

            return {
              questionId,
              selectedOptions: selectedOptions,
              timeSpent: 0,
            };
          }
        });

      // Add separate responses for "Other" text answers
      Object.entries(answers).forEach(([questionId, answer]) => {
        if (questionId.endsWith("_other_text") && answer && answer.trim()) {
          // Extract the parent question ID (remove '_other_text' suffix)
          const parentQuestionId = questionId.replace("_other_text", "");

          // Create a separate response for the "Other" text
          responses.push({
            questionId: parentQuestionId,
            selectedOptions: [],
            essayAnswer: answer.trim(),
            timeSpent: 0,
          });
        }
      });

      console.log("🔍 About to call submitAttemptMutation with:", {
        attemptId,
        responses,
        name,
      });

      // Debug: Log each response to see the format
      responses.forEach((response, index) => {
        console.log(`🔍 Response ${index + 1}:`, {
          questionId: response.questionId,
          questionType: effectiveQuestions.find(
            (q) => q.id === response.questionId
          )?.type,
          selectedOptions: response.selectedOptions,
          essayAnswer: response.essayAnswer,
          timeSpent: response.timeSpent,
        });
      });

      submitAttemptMutation.mutate({
        attemptId,
        responses,
        name,
      });
    }
  };

  // Handle errors - but use dummy data instead
  if (examError && !examData) {
    // console.log('🔍 Using dummy data due to API error:', {
    //   examError: examError,
    //   usingDummyData: true,
    //   dummyQuestionsCount: finalQuestions.length
    // });
    // Don't return error, continue with dummy data
  }

  // Skip loading state - start exam immediately with dummy data
  if (examLoading && !examData) {
    // console.log('🎯 ExamInterface: Using dummy data immediately:', {
    //   examLoading: examLoading,
    //   examData: examData,
    //   examError: examError,
    //   effectiveQuestions: effectiveQuestions?.length,
    //   usingDummyData: true
    // });
    // Continue with dummy data instead of returning null
  }

  // Check if attempts exceeded
  const attemptsInfo = exam.attemptsInfo;
  if (attemptsInfo && !attemptsInfo.canTakeExam) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, var(--danger-50) 0%, var(--warning-50) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "48px",
            boxShadow: "var(--shadow)",
            textAlign: "center",
            maxWidth: "500px",
            width: "100%",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              marginBottom: "24px",
            }}
          >
            ⚠️
          </div>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "var(--danger-700)",
              marginBottom: "16px",
            }}
          >
            Attempts Exceeded
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "var(--secondary-600)",
              marginBottom: "32px",
              lineHeight: "1.6",
            }}
          >
            You have used all {attemptsInfo.attemptsUsed}/
            {attemptsInfo.attemptsAllowed} attempts for this exam. No more
            attempts are allowed.
          </p>

          <div
            style={{
              background: "var(--danger-50)",
              border: "1px solid var(--danger-200)",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "var(--danger-700)",
                marginBottom: "12px",
              }}
            >
              What you can do:
            </h3>
            <ul
              style={{
                textAlign: "left",
                fontSize: "14px",
                color: "var(--danger-600)",
                lineHeight: "1.8",
              }}
            >
              <li>📊 View your previous exam results</li>
              <li>📝 Take other available exams</li>
              <li>📈 Check your performance analytics</li>
              <li>🏆 View your certificates</li>
            </ul>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              className="btn btn-primary"
              onClick={() => navigate("/student/tests")}
            >
              📝 Browse Other Exams
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/student/history")}
            >
              📊 View Exam History
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, var(--success-50) 0%, var(--primary-50) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "48px",
            boxShadow: "var(--shadow)",
            textAlign: "center",
            maxWidth: "500px",
            width: "100%",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              marginBottom: "24px",
            }}
          >
            ✅
          </div>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "var(--success-700)",
              marginBottom: "16px",
            }}
          >
            Exam Submitted Successfully!
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "var(--secondary-600)",
              marginBottom: "32px",
              lineHeight: "1.6",
            }}
          >
            Your answers have been recorded and your exam is being processed.
            You'll be redirected to your detailed results in a few seconds.
          </p>

          <div
            style={{
              background: "var(--success-50)",
              border: "1px solid var(--success-200)",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "var(--success-700)",
                marginBottom: "12px",
              }}
            >
              What happens next?
            </h3>
            <ul
              style={{
                textAlign: "left",
                fontSize: "14px",
                color: "var(--success-600)",
                lineHeight: "1.8",
              }}
            >
              <li>✅ Your answers are being scored</li>
              <li>📊 Detailed analytics are being calculated</li>
              <li>📈 Performance metrics are being generated</li>
              <li>🎯 Difficulty breakdown is being analyzed</li>
              <li>🏆 Certificate will be issued if you passed</li>
            </ul>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              color: "var(--secondary-500)",
              fontSize: "14px",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid var(--primary-300)",
                borderTop: "2px solid var(--primary-600)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            Redirecting to results...
          </div>
        </div>
      </div>
    );
  }

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 1800);
    const minutes = Math.floor((seconds % 1800) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Remove duplicate declaration - currentQuestionData is already defined in state
  // const currentQuestionData = questions[currentQuestion];

  // console.log('🔍 Current Question Debug:', {
  //   currentQuestion,
  //   questionsLength: questions.length,
  //   currentQuestionData,
  //   questions
  // });

  // Skip loading state - start exam immediately
  if (!effectiveQuestions.length) {
    // console.log('🎯 ExamInterface: No questions debug:', {
    //   effectiveQuestionsLength: effectiveQuestions?.length,
    //   effectiveQuestions: effectiveQuestions,
    //   examData: examData,
    //   examLoading: examLoading,
    //   examError: examError
    // });
    // console.log('🎯 ExamInterface: No questions available, skipping loading state');
    return null;
  }

  // ACCA Exam Detection - We don't need this since we're enhancing the existing interface
  // The ACCA styling will be applied automatically when isACCAExam is true

  // Remove duplicate function declarations - these are already defined above
  // const toggleQuestionFlag = (questionIndex) => { ... };
  // Remove duplicate useEffect hooks - these are already defined above

  // Remove duplicate - handleAnswerSelect is already defined above
  // const handleAnswerSelect = (questionId, answerIndex) => {
  //   setAnswers(prev => ({
  //     ...prev,
  //     [questionId]: answerIndex
  //   }));
  // };

  // Remove duplicate - handleFillInTheBlankAnswer is already defined above

  // Remove duplicate - handleSubmit is already defined above

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isAccaExam
          ? "#f8fafc"
          : isIeltsExam
          ? "#f0f8ff"
          : isToeflExam
          ? "#fff8f0"
          : isInternshipExam
          ? "#f0fdf4"
          : "var(--background)",
        fontFamily: isAccaExam
          ? "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          : isIeltsExam
          ? "'Arial', 'Helvetica Neue', sans-serif"
          : isToeflExam
          ? "'Georgia', 'Times New Roman', serif"
          : isInternshipExam
          ? "'Inter', 'Segoe UI', sans-serif"
          : "inherit",
      }}
    >
      {/* Hidden video element used for capturing webcam frames (not visible to user) */}
      <video
        ref={webcamVideoRef}
        muted
        playsInline
        style={{
          position: "fixed",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      {/* Webcam consent modal - REMOVED */}
      {/* {showWebcamConsent && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '1rem',
              maxWidth: 520,
              width: '90%',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.35)',
              padding: '24px 24px 20px',
            }}
          >
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 12,
              }}
            >
              Allow webcam monitoring?
            </h2>
            <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 12 }}>
              Your webcam can be used during this exam for monitoring purposes. This helps proctors verify
              identity and reduce cheating.
            </p>
            <ul
              style={{
                paddingLeft: 18,
                marginBottom: 16,
                color: '#374151',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <li>Only authorized admins/proctors can view the monitoring stream.</li>
              <li>You may see a browser popup asking for camera permission – please click “Allow”.</li>
              <li>You can continue the exam even if you choose not to enable the webcam.</li>
            </ul>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={() => setShowWebcamConsent(false)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 9999,
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleEnableWebcam}
                style={{
                  padding: '8px 18px',
                  borderRadius: 9999,
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: '#16a34a',
                  color: '#ffffff',
                  boxShadow: '0 10px 20px rgba(22, 163, 74, 0.25)',
                }}
              >
                Allow webcam
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Debug Panel - Commented Out */}
      {/* <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: '#000',
        color: '#00ff00',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 9999,
        fontFamily: 'monospace'
      }}>
        <div>Calculator: {showCalculator ? 'ON' : 'OFF'}</div>
        <div>Navigator: {showQuestionNavigator ? 'ON' : 'OFF'}</div>
        <div>Questions: {effectiveQuestions?.length || 0}</div>
        <div>Current: {currentQuestion + 1}</div>
        <div>ACCA: {isAccaExam ? 'YES' : 'NO'}</div>
        <div>Exam Data: {examData ? 'LOADED' : 'NULL'}</div>
        <div style={{ marginTop: '10px' }}>
          <button 
            onClick={() => setShowCalculator(true)}
            style={{ background: '#00ff00', color: '#000', border: 'none', padding: '5px', marginRight: '5px' }}
          >
            Force Calculator ON
          </button>
          <button 
            onClick={() => setShowQuestionNavigator(true)}
            style={{ background: '#00ff00', color: '#000', border: 'none', padding: '5px', marginRight: '5px' }}
          >
              <button
              onClick={() => {
                console.log('🎯 Manual ACCA trigger clicked');
                setIsAccaExam(true);
                setShowFullScreenQuestion(true);
                // Try to get questions directly
                examAPI.getExamQuestions(testId)
                  .then(response => {
                    console.log('🎯 Manual questions fetch:', response);
                    const questions = response.data?.data?.questions || response.data?.questions || [];
                    if (questions.length > 0) {
                      setDirectQuestions(questions);
                      console.log('🎯 Manual questions loaded:', questions.length);
                    }
                  })
                  .catch(error => {
                    console.log('🎯 Manual questions fetch failed:', error);
                  });
              }}
              style={{ background: '#ff6b35', color: '#000', border: 'none', padding: '5px' }}
            >
              Force ACCA ON
              </button>
            </div>
        </div> */}
      {/* ACCA Interface - All ACCA modals and functionality */}
      {isAccaExam && (
        <>
          <ACCAInterface
            showFullScreenQuestion={showFullScreenQuestion}
            setShowFullScreenQuestion={setShowFullScreenQuestion}
            currentQuestion={currentQuestion}
            setCurrentQuestion={setCurrentQuestion}
            questions={effectiveQuestions}
            currentQuestionData={currentQuestionData}
            timeLeft={timeLeft}
            examProgress={examProgress}
            flaggedQuestions={flaggedQuestions}
            toggleQuestionFlag={toggleQuestionFlag}
            answers={answers}
            handleAnswerSelect={handleAnswerSelect}
            handleFillInTheBlankAnswer={handleFillInTheBlankAnswer}
            showInstructions={showInstructions}
            toggleInstructions={toggleInstructions}
            showCalculator={showCalculator}
            setShowCalculator={setShowCalculator}
            showQuestionNavigator={showQuestionNavigator}
            setShowQuestionNavigator={setShowQuestionNavigator}
            exam={exam}
            isAccaExam={isAccaExam}
          />
        </>
      )}

      {/* Internship Interface - Professional exam interface for all exams */}
      {(isInternshipExam || (!isAccaExam && !isIeltsExam && !isToeflExam)) && (
        <>
          <InternshipInterface
            showFullScreenQuestion={showFullScreenQuestion}
            setShowFullScreenQuestion={setShowFullScreenQuestion}
            currentQuestion={currentQuestion}
            setCurrentQuestion={setCurrentQuestion}
            questions={effectiveQuestions}
            currentQuestionData={currentQuestionData}
            timeLeft={timeLeft}
            examProgress={examProgress}
            flaggedQuestions={flaggedQuestions}
            toggleQuestionFlag={toggleQuestionFlag}
            answers={answers}
            handleAnswerSelect={handleAnswerSelect}
            handleFillInTheBlankAnswer={handleFillInTheBlankAnswer}
            handleSubmit={handleSubmit}
            showInstructions={showInstructions}
            toggleInstructions={toggleInstructions}
            showCalculator={showCalculator}
            setShowCalculator={setShowCalculator}
            showQuestionNavigator={showQuestionNavigator}
            setShowQuestionNavigator={setShowQuestionNavigator}
            exam={exam}
            isInternshipExam={isInternshipExam}
          />
        </>
      )}

      {/* Regular Interface Fallback (hidden when full-screen is active or loading) */}
      {!showFullScreenQuestion &&
        !examLoading &&
        !isAccaExam &&
        !isInternshipExam && (
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "24px",
            }}
          >
            {/* Regular exam interface content */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "32px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(0, 0, 0, 0.05)",
              }}
            >
              <h1>Regular Exam Interface</h1>
              <p>This is the standard exam interface for non-ACCA exams.</p>
            </div>
          </div>
        )}

      {/* Calculator Modal - Root Level */}
      {showCalculator && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "0",
              padding: "30px",
              maxWidth: "450px",
              width: "100%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "3px solid #dc2626",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
                borderBottom: "2px solid #dc2626",
                paddingBottom: "15px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#dc2626",
                  margin: 0,
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
              >
                🧮 ACCA CALCULATOR
              </h2>
              <button
                onClick={() => setShowCalculator(false)}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  width: "35px",
                  height: "35px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                background: "#f8fafc",
                border: "2px solid #dc2626",
                borderRadius: "0",
                padding: "20px",
                marginBottom: "25px",
                fontFamily: "monospace",
                fontSize: "24px",
                textAlign: "right",
                minHeight: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                fontWeight: "600",
              }}
            >
              <span>{calculatorDisplay}</span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
                marginBottom: "15px",
              }}
            >
              {[
                "C",
                "±",
                "%",
                "÷",
                "7",
                "8",
                "9",
                "×",
                "4",
                "5",
                "6",
                "-",
                "1",
                "2",
                "3",
                "+",
                "0",
                ".",
                "=",
              ].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleCalculatorInput(btn)}
                  style={{
                    background:
                      btn === "="
                        ? "#dc2626"
                        : btn === "C"
                        ? "#ef4444"
                        : ["+", "-", "×", "÷", "±", "%"].includes(btn)
                        ? "#fbbf24"
                        : "#f1f5f9",
                    color:
                      btn === "="
                        ? "white"
                        : btn === "C"
                        ? "white"
                        : ["+", "-", "×", "÷", "±", "%"].includes(btn)
                        ? "#92400e"
                        : "#1f2937",
                    border: "2px solid #e5e7eb",
                    padding: "18px 8px",
                    borderRadius: "0",
                    fontSize: "18px",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    gridColumn: btn === "0" ? "span 2" : "span 1",
                  }}
                  onMouseEnter={(e) => {
                    if (btn !== "=") {
                      e.target.style.background = "#e5e7eb";
                      e.target.style.borderColor = "#dc2626";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (btn !== "=") {
                      e.target.style.background = "#f1f5f9";
                      e.target.style.borderColor = "#e5e7eb";
                    }
                  }}
                >
                  {btn}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Question Navigator Modal - Root Level */}
      {showQuestionNavigator && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "0",
              padding: "30px",
              maxWidth: "900px",
              width: "100%",
              maxHeight: "85vh",
              overflow: "auto",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "3px solid #dc2626",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
                borderBottom: "2px solid #dc2626",
                paddingBottom: "15px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#dc2626",
                  margin: 0,
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
              >
                🧭 ACCA QUESTION NAVIGATOR
              </h2>
              <button
                onClick={() => setShowQuestionNavigator(false)}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  width: "35px",
                  height: "35px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
                gap: "15px",
                marginBottom: "25px",
              }}
            >
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "10px",
                  background: "#f3f4f6",
                  border: "2px solid #dc2626",
                  borderRadius: "0",
                  marginBottom: "15px",
                }}
              >
                <strong>Total Questions: {questions.length}</strong> |
                <strong>Current: {currentQuestion + 1}</strong> |
                <strong>Answered: {Object.keys(answers).length}</strong>
              </div>
              {questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => navigateToQuestion(index)}
                  style={{
                    background:
                      index === currentQuestion
                        ? "#dc2626"
                        : answers[question.id]
                        ? "#059669"
                        : flaggedQuestions.has(index)
                        ? "#d97706"
                        : "#f3f4f6",
                    color:
                      index === currentQuestion
                        ? "white"
                        : answers[question.id]
                        ? "white"
                        : "#374151",
                    border: `3px solid ${
                      index === currentQuestion
                        ? "#dc2626"
                        : answers[question.id]
                        ? "#059669"
                        : flaggedQuestions.has(index)
                        ? "#d97706"
                        : "#d1d5db"
                    }`,
                    padding: "15px 10px",
                    borderRadius: "0",
                    fontSize: "16px",
                    fontWeight: "800",
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s ease",
                    minHeight: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentQuestion) {
                      e.target.style.transform = "scale(1.05)";
                      e.target.style.boxShadow =
                        "0 10px 25px rgba(220, 38, 38, 0.3)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentQuestion) {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow = "none";
                    }
                  }}
                >
                  {index + 1}
                  {flaggedQuestions.has(index) && (
                    <span
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        fontSize: "12px",
                        color: "#dc2626",
                      }}
                    >
                      🚩
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "center",
                flexWrap: "wrap",
                padding: "20px",
                background: "#f9fafb",
                border: "2px solid #e5e7eb",
                borderRadius: "0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#f3f4f6",
                    border: "3px solid #d1d5db",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Unanswered</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#059669",
                    border: "3px solid #059669",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Answered</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#dc2626",
                    border: "3px solid #dc2626",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Current</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#d97706",
                    border: "3px solid #d97706",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Flagged</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IELTS Full-Screen Question Modal */}
      {isIeltsExam && showFullScreenQuestion && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "#ffffff",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* IELTS Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
              color: "white",
              padding: "20px 30px",
              borderBottom: "3px solid #1d4ed8",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 4px 20px rgba(30, 64, 175, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <div
                style={{
                  background: "#fbbf24",
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1e40af",
                }}
              >
                🎯
              </div>
              <div>
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "800",
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  IELTS {exam?.title}
                </h1>
                <p
                  style={{
                    fontSize: "14px",
                    margin: 0,
                    opacity: 0.9,
                    fontWeight: "600",
                  }}
                >
                  International English Language Testing System
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
              }}
            >
              {/* Question Counter */}
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "4px",
                  }}
                >
                  Question
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: "#fbbf24",
                  }}
                >
                  {currentQuestion + 1} / {questions.length}
                </div>
              </div>

              {/* Timer */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "2px solid #1d4ed8",
                  textAlign: "center",
                  minWidth: "120px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "4px",
                  }}
                >
                  Time Left
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    color: "#fbbf24",
                  }}
                >
                  {Math.floor(timeLeft / 60)}:
                  {(timeLeft % 60).toString().padStart(2, "0")}
                </div>
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  width: "180px",
                  height: "8px",
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  overflow: "hidden",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <div
                  style={{
                    width: `${examProgress}%`,
                    height: "100%",
                    background:
                      "linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)",
                    transition: "width 0.3s ease",
                    borderRadius: "4px",
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* IELTS Question Area */}
          <div
            style={{
              flex: 1,
              padding: "40px 50px",
              background: "#ffffff",
              overflow: "auto",
            }}
          >
            {/* Question Content */}
            <div
              style={{
                background: "#f8fafc",
                border: "3px solid #e5e7eb",
                borderRadius: "16px",
                padding: "35px",
                marginBottom: "30px",
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  lineHeight: "1.7",
                  color: "#1f2937",
                  fontWeight: "500",
                  marginBottom: "25px",
                }}
              >
                {questions[currentQuestion]?.text}
              </div>

              {/* Question Options */}
              {questions[currentQuestion]?.options && (
                <div style={{ display: "grid", gap: "15px" }}>
                  {questions[currentQuestion].options.map((option, index) => (
                    <label
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        padding: "18px 20px",
                        background:
                          answers[questions[currentQuestion].id] === index
                            ? "#dbeafe"
                            : "#ffffff",
                        border: `3px solid ${
                          answers[questions[currentQuestion].id] === index
                            ? "#1e40af"
                            : "#e5e7eb"
                        }`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow:
                          answers[questions[currentQuestion].id] === index
                            ? "0 4px 15px rgba(30, 64, 175, 0.2)"
                            : "0 2px 8px rgba(0, 0, 0, 0.05)",
                      }}
                      onMouseEnter={(e) => {
                        if (answers[questions[currentQuestion].id] !== index) {
                          e.target.style.borderColor = "#1e40af";
                          e.target.style.background = "#f0f9ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (answers[questions[currentQuestion].id] !== index) {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.background = "#ffffff";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={index}
                        checked={
                          answers[questions[currentQuestion].id] === index
                        }
                        onChange={() =>
                          handleAnswerSelect(
                            questions[currentQuestion].id,
                            index
                          )
                        }
                        style={{
                          width: "20px",
                          height: "20px",
                          accentColor: "#1e40af",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        {option.text}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "30px",
                borderTop: "2px solid #e5e7eb",
              }}
            >
              <button
                onClick={() => navigateToQuestion(currentQuestion - 1)}
                disabled={currentQuestion === 0}
                style={{
                  background:
                    currentQuestion === 0
                      ? "#f3f4f6"
                      : "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
                  color: currentQuestion === 0 ? "#9ca3af" : "white",
                  border: "none",
                  padding: "15px 30px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  cursor: currentQuestion === 0 ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow:
                    currentQuestion === 0
                      ? "none"
                      : "0 6px 20px rgba(30, 64, 175, 0.3)",
                  transition: "all 0.2s ease",
                }}
              >
                ← Previous
              </button>

              <button
                onClick={() => navigateToQuestion(currentQuestion + 1)}
                disabled={currentQuestion === questions.length - 1}
                style={{
                  background:
                    currentQuestion === questions.length - 1
                      ? "#f3f4f6"
                      : "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
                  color:
                    currentQuestion === questions.length - 1
                      ? "#9ca3af"
                      : "white",
                  border: "none",
                  padding: "15px 30px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  cursor:
                    currentQuestion === questions.length - 1
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow:
                    currentQuestion === questions.length - 1
                      ? "none"
                      : "0 6px 20px rgba(30, 64, 175, 0.3)",
                  transition: "all 0.2s ease",
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOEFL Full-Screen Question Modal */}
      {isToeflExam && showFullScreenQuestion && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "#ffffff",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* TOEFL Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
              color: "white",
              padding: "20px 30px",
              borderBottom: "3px solid #6d28d9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 4px 20px rgba(124, 58, 237, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <div
                style={{
                  background: "#fbbf24",
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#7c3aed",
                }}
              >
                🌍
              </div>
              <div>
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "800",
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  TOEFL {exam?.title}
                </h1>
                <p
                  style={{
                    fontSize: "14px",
                    margin: 0,
                    opacity: 0.9,
                    fontWeight: "600",
                  }}
                >
                  Test of English as a Foreign Language
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
              }}
            >
              {/* Question Counter */}
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "4px",
                  }}
                >
                  Question
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: "#fbbf24",
                  }}
                >
                  {currentQuestion + 1} / {questions.length}
                </div>
              </div>

              {/* Timer */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "2px solid #6d28d9",
                  textAlign: "center",
                  minWidth: "120px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "4px",
                  }}
                >
                  Time Left
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    color: "#fbbf24",
                  }}
                >
                  {Math.floor(timeLeft / 60)}:
                  {(timeLeft % 60).toString().padStart(2, "0")}
                </div>
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  width: "180px",
                  height: "8px",
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  overflow: "hidden",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <div
                  style={{
                    width: `${examProgress}%`,
                    height: "100%",
                    background:
                      "linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)",
                    transition: "width 0.3s ease",
                    borderRadius: "4px",
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* TOEFL Question Area */}
          <div
            style={{
              flex: 1,
              padding: "40px 50px",
              background: "#ffffff",
              overflow: "auto",
            }}
          >
            {/* Question Content */}
            <div
              style={{
                background: "#faf5ff",
                border: "3px solid #e9d5ff",
                borderRadius: "16px",
                padding: "35px",
                marginBottom: "30px",
                boxShadow: "0 4px 15px rgba(124, 58, 237, 0.05)",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  lineHeight: "1.7",
                  color: "#1f2937",
                  fontWeight: "500",
                  marginBottom: "25px",
                }}
              >
                {questions[currentQuestion]?.text}
              </div>

              {/* Question Options */}
              {questions[currentQuestion]?.options && (
                <div style={{ display: "grid", gap: "15px" }}>
                  {questions[currentQuestion].options.map((option, index) => (
                    <label
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        padding: "18px 20px",
                        background:
                          answers[questions[currentQuestion].id] === index
                            ? "#f3e8ff"
                            : "#ffffff",
                        border: `3px solid ${
                          answers[questions[currentQuestion].id] === index
                            ? "#7c3aed"
                            : "#e9d5ff"
                        }`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow:
                          answers[questions[currentQuestion].id] === index
                            ? "0 4px 15px rgba(124, 58, 237, 0.2)"
                            : "0 2px 8px rgba(0, 0, 0, 0.05)",
                      }}
                      onMouseEnter={(e) => {
                        if (answers[questions[currentQuestion].id] !== index) {
                          e.target.style.borderColor = "#7c3aed";
                          e.target.style.background = "#faf5ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (answers[questions[currentQuestion].id] !== index) {
                          e.target.style.borderColor = "#e9d5ff";
                          e.target.style.background = "#ffffff";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={index}
                        checked={
                          answers[questions[currentQuestion].id] === index
                        }
                        onChange={() =>
                          handleAnswerSelect(
                            questions[currentQuestion].id,
                            index
                          )
                        }
                        style={{
                          width: "20px",
                          height: "20px",
                          accentColor: "#7c3aed",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        {option.text}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "30px",
                borderTop: "2px solid #e9d5ff",
              }}
            >
              <button
                onClick={() => navigateToQuestion(currentQuestion - 1)}
                disabled={currentQuestion === 0}
                style={{
                  background:
                    currentQuestion === 0
                      ? "#f3f4f6"
                      : "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                  color: currentQuestion === 0 ? "#9ca3af" : "white",
                  border: "none",
                  padding: "15px 30px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  cursor: currentQuestion === 0 ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow:
                    currentQuestion === 0
                      ? "none"
                      : "0 6px 20px rgba(124, 58, 237, 0.3)",
                  transition: "all 0.2s ease",
                }}
              >
                ← Previous
              </button>

              <button
                onClick={() => navigateToQuestion(currentQuestion + 1)}
                disabled={currentQuestion === questions.length - 1}
                style={{
                  background:
                    currentQuestion === questions.length - 1
                      ? "#f3f4f6"
                      : "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                  color:
                    currentQuestion === questions.length - 1
                      ? "#9ca3af"
                      : "white",
                  border: "none",
                  padding: "15px 30px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  cursor:
                    currentQuestion === questions.length - 1
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow:
                    currentQuestion === questions.length - 1
                      ? "none"
                      : "0 6px 20px rgba(124, 58, 237, 0.3)",
                  transition: "all 0.2s ease",
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculator Modal - Root Level */}
      {showCalculator && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "0",
              padding: "30px",
              maxWidth: "450px",
              width: "100%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "3px solid #dc2626",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
                borderBottom: "2px solid #dc2626",
                paddingBottom: "15px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#dc2626",
                  margin: 0,
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
              >
                🧮 ACCA CALCULATOR
              </h2>
              <button
                onClick={() => setShowCalculator(false)}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  width: "35px",
                  height: "35px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                background: "#f8fafc",
                border: "2px solid #dc2626",
                borderRadius: "0",
                padding: "20px",
                marginBottom: "25px",
                fontFamily: "monospace",
                fontSize: "24px",
                textAlign: "right",
                minHeight: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                fontWeight: "600",
              }}
            >
              <span>{calculatorDisplay}</span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
                marginBottom: "15px",
              }}
            >
              {[
                "C",
                "±",
                "%",
                "÷",
                "7",
                "8",
                "9",
                "×",
                "4",
                "5",
                "6",
                "-",
                "1",
                "2",
                "3",
                "+",
                "0",
                ".",
                "=",
              ].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleCalculatorInput(btn)}
                  style={{
                    background:
                      btn === "="
                        ? "#dc2626"
                        : btn === "C"
                        ? "#ef4444"
                        : ["+", "-", "×", "÷", "±", "%"].includes(btn)
                        ? "#fbbf24"
                        : "#f1f5f9",
                    color:
                      btn === "="
                        ? "white"
                        : btn === "C"
                        ? "white"
                        : ["+", "-", "×", "÷", "±", "%"].includes(btn)
                        ? "#92400e"
                        : "#1f2937",
                    border: "2px solid #e5e7eb",
                    padding: "18px 8px",
                    borderRadius: "0",
                    fontSize: "18px",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    gridColumn: btn === "0" ? "span 2" : "span 1",
                  }}
                  onMouseEnter={(e) => {
                    if (btn !== "=") {
                      e.target.style.background = "#e5e7eb";
                      e.target.style.borderColor = "#dc2626";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (btn !== "=") {
                      e.target.style.background = "#f1f5f9";
                      e.target.style.borderColor = "#e5e7eb";
                    }
                  }}
                >
                  {btn}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Question Navigator Modal - Root Level */}
      {showQuestionNavigator && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "0",
              padding: "30px",
              maxWidth: "900px",
              width: "100%",
              maxHeight: "85vh",
              overflow: "auto",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "3px solid #dc2626",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
                borderBottom: "2px solid #dc2626",
                paddingBottom: "15px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#dc2626",
                  margin: 0,
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
              >
                🧭 ACCA QUESTION NAVIGATOR
              </h2>
              <button
                onClick={() => setShowQuestionNavigator(false)}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  width: "35px",
                  height: "35px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
                gap: "15px",
                marginBottom: "25px",
              }}
            >
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "10px",
                  background: "#f3f4f6",
                  border: "2px solid #dc2626",
                  borderRadius: "0",
                  marginBottom: "15px",
                }}
              >
                <strong>Total Questions: {questions.length}</strong> |
                <strong>Current: {currentQuestion + 1}</strong> |
                <strong>Answered: {Object.keys(answers).length}</strong>
              </div>
              {questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => navigateToQuestion(index)}
                  style={{
                    background:
                      index === currentQuestion
                        ? "#dc2626"
                        : answers[question.id]
                        ? "#059669"
                        : flaggedQuestions.has(index)
                        ? "#d97706"
                        : "#f3f4f6",
                    color:
                      index === currentQuestion
                        ? "white"
                        : answers[question.id]
                        ? "white"
                        : "#374151",
                    border: `3px solid ${
                      index === currentQuestion
                        ? "#dc2626"
                        : answers[question.id]
                        ? "#059669"
                        : flaggedQuestions.has(index)
                        ? "#d97706"
                        : "#d1d5db"
                    }`,
                    padding: "15px 10px",
                    borderRadius: "0",
                    fontSize: "16px",
                    fontWeight: "800",
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s ease",
                    minHeight: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentQuestion) {
                      e.target.style.transform = "scale(1.05)";
                      e.target.style.boxShadow =
                        "0 10px 25px rgba(220, 38, 38, 0.3)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentQuestion) {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow = "none";
                    }
                  }}
                >
                  {index + 1}
                  {flaggedQuestions.has(index) && (
                    <span
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        fontSize: "12px",
                        color: "#dc2626",
                      }}
                    >
                      🚩
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "center",
                flexWrap: "wrap",
                padding: "20px",
                background: "#f9fafb",
                border: "2px solid #e5e7eb",
                borderRadius: "0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#f3f4f6",
                    border: "3px solid #d1d5db",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Unanswered</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#059669",
                    border: "3px solid #059669",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Answered</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#dc2626",
                    border: "3px solid #dc2626",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Current</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    height: "25px",
                    backgroundColor: "#d97706",
                    border: "3px solid #d97706",
                    borderRadius: "0",
                  }}
                ></div>
                <span>Flagged</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Webcam Consent Modal - REMOVED */}
      {/* Commented out to remove webcam requirement */}
    </div>
  );
};

export default ExamInterface;
